/**
 * Tagada Rx — clinical cases list.
 */

'use client';

import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { TableCard } from '@/components/ui/table-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { RxCaseStatusBadge, RxPageShell } from '@/components/rx';
import {
  formatCents,
  useRxCases,
  useRxIntegrationStatus,
  type RxCaseStatus,
} from '@/lib/api/rx';

const STATUS_OPTIONS: { value: RxCaseStatus | 'all'; labelKey: string }[] = [
  { value: 'all', labelKey: 'cases.status.all' },
  { value: 'requested', labelKey: 'cases.status.requested' },
  { value: 'submitted', labelKey: 'cases.status.submitted' },
  { value: 'in_review', labelKey: 'cases.status.inReview' },
  { value: 'approved', labelKey: 'cases.status.approved' },
  { value: 'shipped', labelKey: 'cases.status.shipped' },
  { value: 'delivered', labelKey: 'cases.status.delivered' },
  { value: 'declined', labelKey: 'cases.status.declined' },
  { value: 'cancelled', labelKey: 'cases.status.cancelled' },
  { value: 'error', labelKey: 'cases.status.error' },
];

const PAGE_SIZE = 25;

export default function RxCasesPage() {
  const { t } = useTranslation('rx');
  const { id: storeId, storeRoutes } = useStoreNavigate();
  const [statusFilter, setStatusFilter] = React.useState<RxCaseStatus | 'all'>('all');
  const [page, setPage] = React.useState(0);

  const integrationStatus = useRxIntegrationStatus(storeId);
  const casesQuery = useRxCases({
    storeId,
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const rxActive = integrationStatus.data?.state === 'active';
  const rows = casesQuery.data?.rows ?? [];
  const total = casesQuery.data?.total ?? 0;

  return (
    <RxPageShell
      title={t('cases.title')}
      description={t('cases.description')}
      actions={
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v as RxCaseStatus | 'all');
            setPage(0);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
    >
      {!rxActive ? (
        <div className="text-muted-foreground rounded-lg border px-4 py-6 text-sm">
          {t('cases.notActiveMessage')}{' '}
          <Link to={storeRoutes.RX_OVERVIEW()} className="underline">
            {t('cases.activateItLink')}
          </Link>{' '}
          {t('cases.notActiveTail')}
        </div>
      ) : (
        <TableCard>
          {casesQuery.isLoading ? (
            <div className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('cases.loading')}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-muted-foreground space-y-1 p-6 text-sm">
              <p>{t('cases.noCasesTitle')}</p>
              <p>
                {t('cases.noCasesDescriptionStart')}{' '}
                <Link className="underline" to={storeRoutes.RX_PRODUCT_OFFERINGS()}>
                  {t('cases.noCasesDescriptionLink')}
                </Link>
                {t('cases.noCasesDescriptionEnd')}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('cases.columns.case')}</TableHead>
                    <TableHead>{t('cases.columns.order')}</TableHead>
                    <TableHead>{t('cases.columns.status')}</TableHead>
                    <TableHead>{t('cases.columns.amount')}</TableHead>
                    <TableHead>{t('cases.columns.nextCharge')}</TableHead>
                    <TableHead>{t('cases.columns.created')}</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} className="hover:bg-muted/30">
                      <TableCell className="font-mono text-xs">{row.id}</TableCell>
                      <TableCell className="font-mono text-xs">{row.orderId}</TableCell>
                      <TableCell>
                        <RxCaseStatusBadge status={row.status} />
                      </TableCell>
                      <TableCell>{formatCents(row.totalAmountMinor, row.currency)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.scheduledNextChargeAt
                          ? new Date(row.scheduledNextChargeAt).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(row.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="ghost" size="sm">
                          <Link to={storeRoutes.RX_CASE_DETAIL(row.id)}>
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="text-muted-foreground flex items-center justify-between px-1 pt-3 text-xs">
                <span>
                  {t('cases.pagination', { count: rows.length, total, page: page + 1 })}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    {t('cases.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * PAGE_SIZE >= total}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    {t('cases.next')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </TableCard>
      )}
    </RxPageShell>
  );
}
